import React, { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip, KV, useModuleTab } from '../../components/ModuleFrame';
import { updateEntity, useEntityList, friendlyError } from '../../data/entities';
import { savePolicy, usePolicyRevisions } from '../../data/policy';
import PolicyHistory from './PolicyHistory';
import { PERMISSIONS, permissionRole } from '../../data/permissions';
import { useLiveConfig } from '../../data/firestore';
import { displayedOrgPlan } from '../../data/displayedPlan';
import MembersModule from './MembersModule';
import { SsoPanels, AuditExport, DomainsPanel } from '../../components/EnterpriseIdentity';
import { apiPost } from '../../utils/backendApi';
import ModelsPanel from './ModelsPanel';
import VaultPanel from './VaultPanel';
import RequirementsEditor from '../../components/RequirementsEditor';

// Administration — structure (org → workspaces → members), users & roles
// with capabilities on top of the rules role, environment controls (who may
// release where — enforced by the deploy endpoint), the audit trail the
// backend writes, and the identity card. SSO/MFA are wave 2: the card says
// so instead of pretending.

const ROLES = ['viewer', 'member', 'admin', 'owner'];
const CAPABILITIES = [
  ['builder', 'Builder', 'App Builder, Knowledge, Workflows, QA, Deployments'],
  ['reviewer', 'Reviewer', 'Human Review queues and decisions'],
  ['appUser', 'App user', 'NoVA channels only (no console build surfaces)'],
];
const ENVS = [['sandbox', 'Sandbox', 'member'], ['uat', 'UAT', 'admin'], ['prod', 'Production', 'admin']];

export default function AdministrationModule() {
  const { org, myRole } = useOutletContext();
  const isAdmin = ['admin', 'owner'].includes(myRole);
  const [tab, setTab] = useModuleTab('structure');
  const { docs: members } = useEntityList('member', { org: org.id }, { orderBy: 'createdAt', dir: 'asc' });
  const { docs: workspaces } = useEntityList('workspace', { org: org.id });
  const { docs: apps } = useEntityList('app', { org: org.id });
  const { docs: audit } = useEntityList('auditEvent', { org: org.id });
  const { revisions } = usePolicyRevisions(org.id, null, { enabled: isAdmin });
  const { doc: liveCfg } = useLiveConfig();
  const [err, setErr] = useState('');
  const planName = displayedOrgPlan(org, {
    plans: liveCfg ? (liveCfg.billing?.plans || {}) : null,
    freeEntitlements: liveCfg?.billing?.freeEntitlements ?? null,
  }).name || '—';

  // Policy maps go through the policy API: each save is a numbered revision
  // (+ audit row) that the Policy history tab shows and can restore.
  const savePolicyArea = async (area, value) => {
    setErr('');
    try { await savePolicy(org.id, area, value); } catch (e) { setErr(friendlyError(e)); }
  };
  const setCap = async (m, cap, on) => {
    setErr('');
    const caps = new Set(m.capabilities || []);
    if (on) caps.add(cap); else caps.delete(cap);
    try { await updateEntity('member', { org: org.id }, m.id, { capabilities: Array.from(caps) }); } catch (e) { setErr(friendlyError(e)); }
  };

  return (
    <ModuleFrame
      title="Administration"
      subtitle="Structure · Users & roles · Permissions · Environment controls · Audit trail · Policy history · Vault"
      accent="#9aa0a6"
      note={`Administration, tab ${tab}: ${(members || []).length} members, ${(workspaces || []).length} workspaces, ${(apps || []).length} apps. NoVA: invite_member, set_org_policy (permissions, environment controls, routing — the person confirms), list_policyRevision, list_auditEvent. Vault values are typed only into the Vault form.`}
      help="How the organization is shaped and who may do what. Roles are enforced by the data rules; capabilities shape the console; environment controls gate releases; the audit trail is written server-side; every policy change is a numbered version you can restore."
      context={(
        <>
          <Chip tone="muted">{(members || []).length} members</Chip>
          <Chip tone="muted">{(workspaces || []).length} workspaces</Chip>
          <Chip tone="muted">{(apps || []).length} apps</Chip>
        </>
      )}
      tabs={[
        { id: 'structure', label: 'Structure' },
        { id: 'roles', label: 'Users & roles', count: (members || []).length },
        { id: 'members', label: 'Invites' },
        { id: 'permissions', label: 'Permissions' },
        { id: 'environments', label: 'Environment controls' },
        { id: 'audit', label: 'Audit trail', count: (audit || []).length },
        { id: 'history', label: 'Policy history', count: revisions.length },
        { id: 'models', label: 'Models & credits' },
        { id: 'vault', label: 'Vault' },
        { id: 'identity', label: 'Identity' },
      ]}
      tab={tab}
      onTab={setTab}
      verbs={[
        { label: 'Invite a member', hint: isAdmin ? 'admin' : 'admins only', onClick: () => setTab('members'), primary: true, disabled: !isAdmin },
        { label: 'Set who can release', hint: 'per environment', onClick: () => setTab('environments') },
        { label: 'Audit trail', hint: 'server-written', onClick: () => setTab('audit') },
        { label: 'Policy history', hint: 'versions · restore', onClick: () => setTab('history') },
      ]}
    >
      {err && <p className="cs-form-error" role="alert">{err}</p>}

      {tab === 'structure' && (
        <ModuleFrame.Columns>
          <ModuleFrame.Panel title="Organization" meta={org.id}>
            <KV rows={[['Name', org.name], ['Description', org.description || '—'], ['Owner', (members || []).find((m) => m.uid === org.ownerUid)?.email || org.ownerUid], ['Created', org.createdAt ? new Date(org.createdAt).toLocaleDateString() : '—'], ['Plan', planName]]} />
            {myRole === 'owner' && <TransferOwnership org={org} members={members || []} onError={setErr} />}
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Workspaces" meta="projects / teams">
            <ul className="mf-list">
              {(workspaces || []).map((w) => <li key={w.id} className="mf-row"><span className="mf-row-grow">{w.name}</span><Link to={`/orgs/${org.id}/ws/${w.id}`} className="mf-muted">open →</Link></li>)}
              {!(workspaces || []).length && <li className="mf-muted">none yet — the first workflow creates “Main”</li>}
            </ul>
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Apps" meta="App Builder spine">
            <ul className="mf-list">
              {(apps || []).map((a) => <li key={a.id} className="mf-row"><Chip tone="muted">{a.stage}</Chip><span className="mf-row-grow">{a.name}</span><Link to={`/orgs/${org.id}/builder?app=${a.id}`} className="mf-muted">open →</Link></li>)}
              {!(apps || []).length && <li className="mf-muted">none yet</li>}
            </ul>
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'roles' && (
        <ModuleFrame.Panel title="Role matrix" meta={isAdmin ? 'role = data rules · capabilities = console surfaces' : 'admins edit'}>
          <table className="mf-table mf-matrix">
            <thead><tr><th>Member</th><th>Role</th>{CAPABILITIES.map(([k, l, h]) => <th key={k} title={h}>{l}</th>)}</tr></thead>
            <tbody>
              {(members || []).map((m) => (
                <tr key={m.id}>
                  <td>{m.displayName || m.email}<div className="mf-muted">{m.email}</div></td>
                  <td>
                    {m.uid === org.ownerUid ? <Chip tone="ok">owner</Chip> : (
                      <select className="cs-input" value={m.role} disabled={!isAdmin} onChange={(e) => updateEntity('member', { org: org.id }, m.id, { role: e.target.value }).catch((ex) => setErr(friendlyError(ex)))} aria-label={`role for ${m.email}`}>
                        {ROLES.filter((r) => r !== 'owner').map((r) => <option key={r}>{r}</option>)}
                      </select>
                    )}
                  </td>
                  {CAPABILITIES.map(([k]) => (
                    <td key={k}><input type="checkbox" checked={(m.capabilities || []).includes(k)} disabled={!isAdmin || m.uid === org.ownerUid} onChange={(e) => setCap(m, k, e.target.checked)} aria-label={`${k} for ${m.email}`} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mf-muted" style={{ marginTop: 10 }}>Roles: viewer reads · member builds · admin approves, releases, invites · owner everything. Capabilities are hints for the console (and the audit) — the rules role is what the data enforces.</p>
        </ModuleFrame.Panel>
      )}

      {tab === 'members' && <MembersModule />}

      {tab === 'permissions' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Who may do what" meta="enforced by the data rules and the API — applies within a minute, no deploy">
            <table className="mf-table">
              <thead><tr><th>Action</th><th>Minimum role</th><th /></tr></thead>
              <tbody>
                {PERMISSIONS.map((perm, i) => {
                  const effective = permissionRole(perm.key, org, liveCfg);
                  const platform = permissionRole(perm.key, null, liveCfg);
                  const overridden = org.permissions?.[perm.key] && org.permissions[perm.key] !== platform;
                  const first = i === 0 || PERMISSIONS[i - 1].group !== perm.group;
                  return (
                    <React.Fragment key={perm.key}>
                      {first && <tr><th colSpan={3} style={{ paddingTop: 12 }}>{perm.group}</th></tr>}
                      <tr>
                        <td>{perm.label}<div className="cs-help" style={{ margin: 0 }}><code>{perm.key}</code></div></td>
                        <td>
                          <select className="cs-input" value={effective} disabled={!isAdmin} aria-label={`minimum role for ${perm.label}`}
                            onChange={(e) => savePolicyArea('permissions', { ...(org.permissions || {}), [perm.key]: e.target.value })}>
                            {['viewer', 'member', 'admin', 'owner'].map((r) => <option key={r}>{r}</option>)}
                          </select>
                        </td>
                        <td>{overridden ? <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!isAdmin} onClick={() => { const next = { ...(org.permissions || {}) }; delete next[perm.key]; savePolicyArea('permissions', next); }}>Reset to {platform}</button> : <span className="cs-help">default</span>}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <p className="mf-muted" style={{ marginTop: 10 }}>Roles: viewer reads · member builds · admin governs · owner everything. Membership, billing, deleting the organization and release approval are not on this list on purpose — those stay with admins and owners (releases: Environment controls).</p>
            <PolicyHistory org={org} isAdmin={isAdmin} area="permissions" compact />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="How it works" meta="three layers, one table">
            <KV rows={[
              ['Data rules', 'Creating or editing agents, apps, documents, workflows and connected systems checks this table in the Firestore rules.'],
              ['API', 'Launching instances, publishing knowledge, Lab actions and the rest check it on every request.'],
              ['Console', 'Buttons appear or hide from the same table — what you can see, you can do.'],
              ['Defaults', 'Compiled into the platform; nventr may adjust them for everyone. Your overrides sit on top.'],
            ]} />
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'environments' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Minimum role to release" meta="enforced by the deploy endpoint">
            <table className="mf-table">
              <thead><tr><th>Environment</th><th>Minimum role</th></tr></thead>
              <tbody>
                {ENVS.map(([k, label, dflt]) => (
                  <tr key={k}>
                    <td>{label}</td>
                    <td>
                      <select className="cs-input" value={org.envControls?.[k] || dflt} disabled={!isAdmin || k === 'sandbox'} onChange={(e) => savePolicyArea('envControls', { ...(org.envControls || {}), [k]: e.target.value })} aria-label={`minimum role for ${label}`}>
                        {['member', 'admin', 'owner'].map((r) => <option key={r}>{r}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mf-muted" style={{ marginTop: 10 }}>Production stays admin+ unless you open it on purpose. Changes apply to the next deploy call.</p>
            <PolicyHistory org={org} isAdmin={isAdmin} area="envControls" compact />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Workflow requirements" meta="every nQube workflow in an environment is checked against these">
            <p className="mf-muted" style={{ marginTop: 0 }}>
              Design requirements the organization sets per environment — a human approval before any write, validation before every write, sensitive data kept away from models, a performance budget. They appear read-only in each workflow&apos;s Requirements panel; a miss blocks runs in UAT and prod.
            </p>
            {ENVS.map(([k, label]) => (
              <div key={k} style={{ marginTop: 12 }}>
                <strong style={{ fontSize: 13 }}>{label}</strong>
                {isAdmin ? (
                  <RequirementsEditor
                    compact
                    rows={org.pipelinePolicy?.[k]?.requirements || []}
                    emptyText="No organization-wide requirements for this environment."
                    onChange={(rows) => savePolicyArea('pipelinePolicy', { ...(org.pipelinePolicy || {}), [k]: { requirements: rows.map((r) => ({ id: r.id, kind: r.kind, text: r.text, params: r.params || {} })) } })}
                  />
                ) : (
                  <ul className="mf-muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {(org.pipelinePolicy?.[k]?.requirements || []).map((r) => <li key={r.id}>{r.text}</li>)}
                    {!(org.pipelinePolicy?.[k]?.requirements || []).length && <li>none</li>}
                  </ul>
                )}
              </div>
            ))}
            <PolicyHistory org={org} isAdmin={isAdmin} area="pipelinePolicy" compact />
          </ModuleFrame.Panel>
          <ModuleFrame.Panel title="Related" meta="other controls">
            <KV rows={[
              ['Guardrails', <Link to={`/orgs/${org.id}/knowledge?tab=guardrails`}>Knowledge → Guardrails</Link>],
              ['Review routing', <Link to={`/orgs/${org.id}/human-review?tab=routing`}>Human Review → Routing rules</Link>],
              ['Billing & plan', <Link to={`/orgs/${org.id}/settings`}>Settings</Link>],
            ]} />
          </ModuleFrame.Panel>
        </ModuleFrame.Columns>
      )}

      {tab === 'audit' && (
        <ModuleFrame.Panel title="Audit trail" meta="server-side mutations in this org (deploys, publishes, access, members)" actions={<AuditExport org={org} isAdmin={isAdmin} />}>
          {!(audit || []).length && <p className="cs-help">No events yet. Deploys, knowledge publishes, integration tests, QA runs, access mints and membership changes land here.</p>}
          <table className="mf-table">
            <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Target</th><th>Detail</th></tr></thead>
            <tbody>
              {(audit || []).slice(0, 200).map((e) => (
                <tr key={e.id}>
                  <td className="mf-muted">{e.createdAt ? new Date(e.createdAt).toLocaleString() : ''}</td>
                  <td><strong>{e.action}</strong></td>
                  <td className="mf-muted">{e.actorEmail || (members || []).find((m) => m.uid === e.actor)?.email || e.actor}</td>
                  <td className="mf-muted">{e.target}</td>
                  <td className="mf-muted" style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, maxWidth: 360, overflowWrap: 'anywhere' }}>{e.meta ? JSON.stringify(e.meta).slice(0, 200) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ModuleFrame.Panel>
      )}

      {tab === 'history' && <PolicyHistory org={org} isAdmin={isAdmin} />}

      {tab === 'models' && (
        <>
          <ModelsPanel orgId={org.id} isAdmin={isAdmin} />
          <PolicyHistory org={org} isAdmin={isAdmin} area="models" compact />
        </>
      )}

      {tab === 'vault' && <VaultPanel orgId={org.id} isAdmin={isAdmin} />}

      {tab === 'identity' && (
        <ModuleFrame.Columns cols={2}>
          <ModuleFrame.Panel title="Identity" meta="how people sign in">
            <KV rows={[
              ['Sign-in', org.sso ? `${org.sso.kind.toUpperCase()} single sign-on${org.sso.enforced ? ' (enforced)' : ''}, Google, Microsoft, or email + password (verified); invite links bind an email to an org role` : 'Google, Microsoft, or email + password with a verified address; invite links bind an email to an org role'],
              ['Domains', (org.verifiedDomains || []).length ? `${org.verifiedDomains.join(', ')} — colleagues who sign up on these request to join here` : 'none verified — colleagues who sign up create their own organization until one is'],
              ['Claims', 'org roles are mirrored into the token for backend checks; rules read membership docs directly'],
              ['Provisioning', org.scim?.tokenHash && org.scim?.enabled !== false ? 'SCIM 2.0 from your IdP + invites' : 'invites (SCIM available below)'],
              ['Instance operators', 'dev-access accounts are minted per member from DevOps → Instances and audited'],
              ['Self-hosted stamps', 'GoTrue carries SAML natively — see the kit selfhost README'],
            ]} />
          </ModuleFrame.Panel>
          <DomainsPanel org={org} isAdmin={isAdmin} />
          <SsoPanels org={org} isAdmin={isAdmin} />
        </ModuleFrame.Columns>
      )}
    </ModuleFrame>
  );
}

// Owner only. The org creator is whoever signed up first — often not the
// person who should own the company's organization. ownerUid is otherwise
// immutable; POST /orgs/:id/transfer-owner moves it to an existing member
// with a verified email and demotes the previous owner to admin (audited).
function TransferOwnership({ org, members, onError }) {
  const [open, setOpen] = useState(false);
  const [toUid, setToUid] = useState('');
  const [busy, setBusy] = useState(false);
  const candidates = members.filter((m) => m.uid !== org.ownerUid);
  const go = async () => {
    const target = candidates.find((m) => m.uid === toUid);
    if (!target) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Transfer ownership of ${org.name} to ${target.email}? You become an admin. This cannot be undone from your side.`)) return;
    setBusy(true); onError('');
    try { await apiPost(`/orgs/${org.id}/transfer-owner`, { toUid }); setOpen(false); } catch (e) { onError(e.message); } finally { setBusy(false); }
  };
  return (
    <div style={{ marginTop: 12 }}>
      {!open && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setOpen(true)} data-agent-noclick>Transfer ownership…</button>}
      {open && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select className="cs-input" style={{ minWidth: 220 }} value={toUid} onChange={(e) => setToUid(e.target.value)} aria-label="New owner">
            <option value="">Choose the new owner…</option>
            {candidates.map((m) => <option key={m.uid} value={m.uid}>{m.displayName || m.email} ({m.role})</option>)}
          </select>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!toUid || busy} onClick={go} data-agent-noclick>{busy ? 'Transferring…' : 'Transfer'}</button>
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          {!candidates.length && <span className="cs-help">Invite the new owner as a member first.</span>}
        </div>
      )}
    </div>
  );
}
